import {Component, Inject, OnDestroy, OnInit, PLATFORM_ID} from '@angular/core';
import {isPlatformBrowser} from '@angular/common';
import {Router} from '@angular/router';
import {Subscription} from 'rxjs';
import {LoginService} from '../../core/components/login/login.service';
import {TranslationService} from '../../core/services/translation.service';
import {NavigationCommand, NavigationService} from '../../data/services/navigation.service';
import {HupSubscriptionsService} from "../../data/services/hup-subscriptions.service";
import {BookableCombiOffer, Qualified4CombinationSubscription} from "../../data/models/qualified-subscription.model";
import {MatListModule} from "@angular/material/list";

/**
 * Anzeigezustand der Zubucherseite.
 */
export enum AdditionalBookingState {
  /** Anmeldestatus noch nicht bekannt - es wird ein neutraler Spinner gezeigt. */
  UNKNOWN = 'UNKNOWN',
  /** Nicht angemeldet - Anmeldung oder Identifikation werden angeboten. */
  GUEST = 'GUEST',
  /** Angemeldet - die zubuchbaren Angebote je Abonnement werden gezeigt (P5W-4460). */
  AUTHENTICATED = 'AUTHENTICATED'
}

/**
 * Übersicht der Zubucher-Angebote: je Hauptabonnement die Kombi-Werbeschlüssel, die der Kunde
 * dazubuchen kann, mit einem Button je Angebot und - bei mehr als einem - "Alle buchen".
 *
 * pca liefert nur Abonnements mit mindestens einem zubuchbaren Angebot; ein bereits gebuchtes
 * Produkt ist dort schon herausgefiltert. Eine leere Liste heißt also schlicht "nichts zuzubuchen".
 */
@Component({
  selector: 'app-additional-booking',
  templateUrl: './additional-booking.component.html',
  styleUrls: ['./additional-booking.component.scss']
})
export class AdditionalBookingComponent implements OnInit, OnDestroy {

  /** Query-Parameter der Bestätigungsseite mit den gewählten Kombi-Werbeschlüsseln. */
  public static readonly SALES_CODE_IDS_PARAM = 'salesCodeIds';

  public justRegistered: boolean = false;

  private state: AdditionalBookingState = AdditionalBookingState.UNKNOWN;
  private authSub: Subscription;
  /** Laufender Listen-Abruf - wird vor jedem neuen Abruf abbestellt, damit keine alte Antwort gewinnt. */
  private listSub: Subscription;
  /** Abmeldung des pageshow-Listeners; null beim Server-Rendering. */
  private removePageShowListener: () => void = null;
  protected qualifiedSubscriptions: Qualified4CombinationSubscription[] = [];
  protected errors: string[] = [];
  protected isLoading = true;

  constructor(public translationService: TranslationService,
              private loginService: LoginService,
              private navigationService: NavigationService,
              private subscriptionService: HupSubscriptionsService,
              private router: Router,
              @Inject(PLATFORM_ID) private platformId: Object) {
    this.justRegistered = !!this.router.getCurrentNavigation()?.extras?.state?.justRegistered;
  }

  ngOnInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      // Beim Server-Rendering ist der Anmeldestatus nicht bekannt. Würde hier der Gast-Zustand
      // gerendert, sähe ein angemeldeter Nutzer kurz die Aufforderung zur Anmeldung.
      return;
    }
    // Der Anmeldestatus kann sich nachträglich ändern (läuft autoLogin/Token-Refresh noch, ist die
    // erste Emission null). Deshalb wird bei jeder Emission neu geladen und nicht einmalig synchron
    // nach dem subscribe – analog HupSubscriptionComponent.
    this.authSub = this.loginService.getAuthData().subscribe(authData => {
      this.state = authData ? AdditionalBookingState.AUTHENTICATED : AdditionalBookingState.GUEST;
      if (!this.isAuthenticated) {
        return;
      }
      this.loadQualifiedSubscriptions();
    });
    this.listenForPageRestore();
  }

  /**
   * Holt die zubuchbaren Abonnements samt Angeboten und setzt dabei den Anzeigezustand zurück.
   *
   * Eigene Methode, weil die Liste an mehr als einem Zeitpunkt frisch sein muss: beim Aufbau der
   * Seite (Anmeldestatus bekannt) und bei der Rückkehr aus dem Back/Forward-Cache - nach jeder
   * Zubuchung fallen gebuchte Angebote heraus.
   */
  private loadQualifiedSubscriptions(): void {
    this.isLoading = true;
    this.errors = [];
    // Ein noch laufender Abruf würde sonst nach dem neuen antworten und die Liste wieder veralten.
    this.listSub?.unsubscribe();
    this.listSub = this.subscriptionService.getQualifiedSubscriptions().subscribe({
      next: qualifiedSubs => {
        // Ein Abo ohne Angebot hat auf dieser Seite nichts verloren, auch wenn pca es liefern sollte.
        this.qualifiedSubscriptions = qualifiedSubs.filter(qualified =>
          !!qualified.subscription && qualified.bookableCombiOffers?.length > 0);
        this.isLoading = false;
      },
      error: () => {
        this.qualifiedSubscriptions = [];
        this.errors = [this.translationService.getTranslation('error_subscription_init')];
        this.isLoading = false;
      }
    });
  }

  /**
   * Lädt die Liste neu, wenn der Browser die Seite aus seinem Back/Forward-Cache wiederherstellt.
   *
   * Bei einem solchen Restore wird die eingefrorene Seite samt JavaScript-Zustand zurückgeholt -
   * Angular baut die Komponente NICHT neu auf, ngOnInit läuft nicht, und es geht kein einziger
   * Request hinaus. Der Kunde sähe also genau die Liste, die vor seiner Zubuchung galt. Nur ein
   * Restore (`persisted`) löst das Nachladen aus; beim normalen Seitenaufbau feuert pageshow
   * ebenfalls, dort hat ngOnInit aber schon geladen.
   */
  private listenForPageRestore(): void {
    const onPageShow = (event: PageTransitionEvent): void => {
      if (!event.persisted || !this.isAuthenticated) {
        return;
      }
      this.loadQualifiedSubscriptions();
    };
    window.addEventListener('pageshow', onPageShow);
    this.removePageShowListener = () => window.removeEventListener('pageshow', onPageShow);
  }

  ngOnDestroy(): void {
    this.removePageShowListener?.();
    this.listSub?.unsubscribe();
    this.authSub?.unsubscribe();
  }

  public get isStateUnknown(): boolean {
    return this.state === AdditionalBookingState.UNKNOWN;
  }

  public get isGuest(): boolean {
    return this.state === AdditionalBookingState.GUEST;
  }

  public get isAuthenticated(): boolean {
    return this.state === AdditionalBookingState.AUTHENTICATED;
  }

  /** Geladen, ohne Fehler, aber kein Abonnement mit zubuchbarem Angebot. */
  public get hasNoEligibleSubscription(): boolean {
    return !this.isLoading && this.errors.length === 0 && this.qualifiedSubscriptions.length === 0;
  }

  /**
   * Führt auf die Bestätigungsseite der Zubuchung - für ein einzelnes Angebot oder ("Alle buchen")
   * für alle Angebote des Abonnements. Die gewählten Werbeschlüssel reisen als Query-Parameter mit;
   * die Bestätigungsseite prüft sie gegen pca, bevor sie sie anzeigt.
   */
  public bookOffers(qualified: Qualified4CombinationSubscription, offers: BookableCombiOffer[]): void {
    this.router.navigate([NavigationCommand.ADDITIONALBOOKINGOVERVIEW, String(qualified.subscription.subId)], {
      queryParams: {[AdditionalBookingComponent.SALES_CODE_IDS_PARAM]: offers.map(offer => offer.salesCodeId)}
    });
  }

  /** Führt in die Abonnement-Übersicht des Kunden. */
  public goToSubscriptions(): void {
    this.navigationService.navigateTo(NavigationCommand.SUBSCRIPTION);
  }

  /** Führt zur Anmeldeseite, die nach erfolgreicher Anmeldung hierher zurückkehrt. */
  public goToLogin(): void {
    this.router.navigate([NavigationCommand.LOGIN], {
      queryParams: {returnUrl: NavigationCommand.ADDITIONALBOOKINGOVERVIEW}
    });
  }

  /** Führt in das Identifikationsformular, in dem die Registrierung erzwungen wird. */
  public goToIdentification(): void {
    this.navigationService.navigateTo(
      NavigationCommand.IDENTIFICATIONREGISTER,
      [NavigationCommand.ADDITIONALBOOKINGOVERVIEW.substring(1)]
    );
  }
}
